export const FETCH_TIMEOUT_MS = 30_000;

// Matches both the platform's 'Network request failed' and our synthesised 'Network request timed out'.
export const IS_NETWORK_ERROR_REGEX = /Network request (failed|timed out)/;

export const fetchWithTimeout: typeof globalThis.fetch = async (
  input,
  init
) => {
  const controller = new AbortController();
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, FETCH_TIMEOUT_MS);

  // Bail immediately if the caller's signal is already aborted. Always an
  // Error named 'AbortError': RetryLink and errorLink skip caller cancels by
  // that name (TA-11017). Not `new DOMException(...)` or `signal.reason`:
  // on RN there is no global DOMException (that line would throw a
  // ReferenceError) and the abort-controller polyfill has no `reason`.
  if (init?.signal?.aborted) {
    clearTimeout(timeoutId);
    throw Object.assign(new Error('The operation was aborted.'), {
      name: 'AbortError',
      cause: init.signal.reason,
    });
  }

  // Forward caller's abort to our controller. Named ref so it can be removed in finally.
  // callerAborted prevents misclassifying an intentional cancel as a timeout when
  // both the timeout and the caller signal fire in the same tick.
  let callerAborted = false;
  const onCallerAbort = () => {
    callerAborted = true;
    controller.abort();
  };
  init?.signal?.addEventListener('abort', onCallerAbort, { once: true });

  try {
    return await globalThis.fetch(input, {
      ...init,
      signal: controller.signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError' && timedOut && !callerAborted) {
      throw new TypeError('Network request timed out');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
    init?.signal?.removeEventListener('abort', onCallerAbort);
  }
};
